/**
 * Xoul Auth State Manager
 *
 * Manages JWT token in localStorage and provides helpers
 * for API calls with authentication.
 */

const API_BASE = window.location.origin
const TOKEN_KEY = 'xoul_token'
const USER_KEY = 'xoul_user'

export const auth = {
    /** Check if JWT token is expired */
    _isTokenExpired(token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]))
            if (!payload.exp) return false
            return Date.now() >= payload.exp * 1000
        } catch {
            return true // 파싱 실패 = 잘못된 토큰
        }
    },

    /** Get stored JWT token (만료 시 자동 logout) */
    getToken() {
        const token = localStorage.getItem(TOKEN_KEY)
        if (token && this._isTokenExpired(token)) {
            this.logout()
            return null
        }
        return token
    },

    /** Get stored user info */
    getUser() {
        // 토큰 만료 체크 먼저
        if (!this.getToken()) return null
        const raw = localStorage.getItem(USER_KEY)
        if (!raw) return null
        try { return JSON.parse(raw) } catch { return null }
    },

    /** Check if user is logged in */
    isLoggedIn() {
        return !!this.getToken()
    },

    /** Save token + user */
    setAuth(token, user) {
        localStorage.setItem(TOKEN_KEY, token)
        if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
    },

    /** Clear auth */
    logout() {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(USER_KEY)
    },

    /** Fetch with auth header */
    async apiFetch(path, options = {}) {
        const token = this.getToken()
        const headers = { 'Content-Type': 'application/json', ...options.headers }
        if (token) headers['Authorization'] = `Bearer ${token}`
        const resp = await fetch(`${API_BASE}${path}`, { ...options, headers })
        return resp
    },

    /** Email signup */
    async signup(email, password, name = '') {
        const resp = await this.apiFetch('/api/auth/signup', {
            method: 'POST',
            body: JSON.stringify({ email, password, name }),
        })
        if (!resp.ok) {
            const err = await resp.json()
            throw new Error(err.detail || 'Signup failed')
        }
        const data = await resp.json()
        this.setAuth(data.token, data.user)
        return data
    },

    /** Email login */
    async login(email, password) {
        const resp = await this.apiFetch('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        })
        if (!resp.ok) {
            const err = await resp.json()
            throw new Error(err.detail || 'Login failed')
        }
        const data = await resp.json()
        this.setAuth(data.token, data.user)
        return data
    },

    /** Get OAuth login URL */
    getOAuthUrl(provider) {
        return `${API_BASE}/api/auth/${provider}`
    },

    /** Fetch current user profile */
    async fetchProfile() {
        const resp = await this.apiFetch('/api/auth/me')
        if (!resp.ok) {
            if (resp.status === 401) this.logout()
            return null
        }
        const user = await resp.json()
        localStorage.setItem(USER_KEY, JSON.stringify(user))
        return user
    },

    /** Update profile */
    async updateProfile(data) {
        const resp = await this.apiFetch('/api/auth/me', {
            method: 'PUT',
            body: JSON.stringify(data),
        })
        if (!resp.ok) throw new Error('Update failed')
        const user = await resp.json()
        localStorage.setItem(USER_KEY, JSON.stringify(user))
        return user
    },
}
